import { motion } from 'framer-motion';
import { 
  Film, 
  ArrowRight,
  CheckCircle,
  FileText,
  Camera,
  Scissors,
  Sparkles,
  Upload,
  Play,
  Mic,
  Monitor,
  Palette
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const ProducaoCursos = ({ onDemoClick }) => {
  const process = [
    {
      step: '01',
      icon: FileText,
      title: 'Roteirização',
      description: 'Desenvolvemos o roteiro do curso com técnicas de storytelling e design instrucional para máximo engajamento.'
    },
    {
      step: '02',
      icon: Camera,
      title: 'Gravação',
      description: 'Captação em estúdio profissional ou na sua empresa, com equipamentos de última geração.'
    },
    {
      step: '03',
      icon: Scissors,
      title: 'Edição',
      description: 'Edição profissional com cortes dinâmicos, legendas, trilha sonora e recursos visuais.'
    },
    {
      step: '04',
      icon: Sparkles,
      title: 'Motion Graphics',
      description: 'Animações, infográficos e elementos visuais que enriquecem o conteúdo.'
    },
    {
      step: '05',
      icon: Upload,
      title: 'Publicação',
      description: 'Entrega nos formatos adequados e publicação direta na sua plataforma EAD.'
    },
  ];

  const formats = [
    {
      icon: Film,
      title: 'Videoaulas',
      description: 'Cursos em vídeo com apresentador, screencast ou animação.'
    },
    {
      icon: Mic,
      title: 'Podcasts',
      description: 'Conteúdo em áudio para aprendizado em qualquer momento.'
    },
    {
      icon: Monitor,
      title: 'E-learning Interativo',
      description: 'Módulos com quiz, simulações e interatividade (SCORM, H5P).'
    },
    {
      icon: Palette,
      title: 'Animações',
      description: 'Conteúdo 100% animado para conceitos complexos ou institucionais.'
    },
  ];

  const portfolio = [
    {
      title: 'Treinamento de Vendas',
      category: 'Comercial',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop',
    },
    {
      title: 'Onboarding Corporativo',
      category: 'RH',
      image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&h=300&fit=crop',
    },
    {
      title: 'Compliance e Ética',
      category: 'Jurídico',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=300&fit=crop',
    },
    {
      title: 'Segurança do Trabalho',
      category: 'SST',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop',
    },
    {
      title: 'Liderança e Gestão',
      category: 'Desenvolvimento',
      image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=300&fit=crop',
    },
    {
      title: 'Produto e Sistema',
      category: 'Tecnologia',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'Preciso ter um roteiro pronto para começar?',
      answer: 'Não! Nossa equipe de design instrucional pode desenvolver todo o roteiro do zero, ou trabalhar com material que você já possui. Adaptamos o processo às suas necessidades.'
    },
    {
      question: 'Vocês gravam na nossa empresa ou no estúdio?',
      answer: 'Ambas as opções! Temos estúdio próprio equipado para gravações de alta qualidade. Também fazemos gravações externas na sua empresa quando o contexto pede imagens do ambiente real.'
    },
    {
      question: 'Qual o prazo médio para produzir um curso?',
      answer: 'Varia conforme a complexidade. Um módulo simples de 5-10 minutos leva cerca de 2-3 semanas. Cursos mais elaborados com animações e interatividade podem levar 4-8 semanas. Fazemos um cronograma detalhado no início do projeto.'
    },
    {
      question: 'Os cursos produzidos funcionam em qualquer plataforma?',
      answer: 'Sim! Entregamos os vídeos em formatos universais (MP4) e também em padrões de e-learning como SCORM quando necessário, garantindo compatibilidade com qualquer LMS.'
    },
    {
      question: 'Vocês fazem cursos interativos?',
      answer: 'Sim! Produzimos módulos interativos com quiz, simulações, cenários de decisão e gamificação usando tecnologias como H5P e SCORM. A interatividade aumenta significativamente o engajamento e retenção.'
    },
  ];

  return (
    <div data-testid="producao-cursos-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur text-sm font-medium mb-6">
                <Film className="h-4 w-4" />
                Produção de Cursos
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Estúdio{' '}
                <span className="text-yellow-300">Profissional</span>{' '}
                para seus Cursos
              </h1>
              <p className="text-lg text-orange-100 mb-8 max-w-xl">
                Do roteiro à publicação, produzimos cursos com qualidade cinematográfica 
                que engajam e geram resultados para sua empresa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onDemoClick}
                  data-testid="producao-hero-demo-btn"
                  className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 rounded-full text-lg font-semibold"
                >
                  Pedir Orçamento
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <img
                  src="https://images.unsplash.com/photo-1769699167679-a3184af4d8f1?w=800&h=500&fit=crop"
                  alt="Estúdio de Gravação CSP"
                  className="rounded-xl w-full"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform">
                    <Play className="h-8 w-8 text-orange-600 ml-1" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Nosso <span className="text-orange-500">Processo</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Um fluxo de trabalho organizado para garantir qualidade e prazos em cada projeto.
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-orange-200" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
              {process.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative text-center"
                >
                  <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500 text-white mb-6 shadow-lg shadow-orange-500/30">
                    <item.icon className="h-7 w-7" />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Formats Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Formatos <span className="text-blue-600">Disponíveis</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Escolha o formato ideal para cada tipo de conteúdo e público.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {formats.map((format, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full p-6 text-center border border-slate-100 hover:border-orange-200 hover:shadow-lg transition-all group">
                  <CardContent className="p-0">
                    <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-100 transition-colors">
                      <format.icon className="h-8 w-8 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{format.title}</h3>
                    <p className="text-slate-600 text-sm">{format.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Tipos de Cursos que <span className="text-orange-500">Produzimos</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Experiência em diversas áreas e segmentos corporativos.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolio.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden group cursor-pointer border-0 shadow-md hover:shadow-xl transition-all">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <span className="inline-block px-2 py-1 rounded bg-orange-500 text-white text-xs font-medium mb-2">
                        {item.category}
                      </span>
                      <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Por que produzir{' '}
                <span className="text-orange-400">com a CSP</span>?
              </h2>
              <ul className="space-y-4">
                {[
                  'Estúdio próprio com equipamentos profissionais',
                  'Equipe especializada em design instrucional',
                  'Entrega nos prazos acordados',
                  'Revisões ilimitadas até aprovação',
                  'Formatos compatíveis com qualquer LMS',
                  'Suporte pós-entrega para ajustes',
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&h=600&fit=crop"
                alt="Equipe de produção"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Perguntas <span className="text-orange-500">Frequentes</span>
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl border-0 px-6"
                data-testid={`producao-faq-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Vamos produzir seu próximo curso?
            </h2>
            <p className="text-lg text-orange-100 mb-8">
              Entre em contato para um orçamento personalizado e conheça nosso estúdio.
            </p>
            <Button
              onClick={onDemoClick}
              data-testid="producao-cta-demo-btn"
              className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 rounded-full text-lg font-semibold"
            >
              Solicitar Orçamento
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
